import pluralize from "pluralize";

type Format = "full" | "abbreviated" | "short";

interface FormatMinutesDurationOptions {
  /**
   * Output format:
   * - "full": "1 hour 30 minutes", "2 hours", "45 minutes"
   * - "abbreviated": "1 h 30 m", "2 h", "45 m"
   * - "short": "1 hr 30 min", "2 hr", "45 min"
   * Default: "short"
   */
  format?: Format;

  /**
   * Separator between the number and the unit label.
   * Default: " "
   */
  separator?: string;
}

const UNIT_LABELS: Record<Format, { hour: string; minute: string }> = {
  full: { hour: "hour", minute: "minute" },
  abbreviated: { hour: "h", minute: "m" },
  short: { hour: "hr", minute: "min" },
};

/**
 * Formats a duration in minutes as hours and minutes.
 * Does not round or convert to larger units — decomposes exactly.
 *
 * @param durationInMinutes - The duration value in minutes
 * @param options - Formatting options
 * @returns A formatted duration string
 *
 * @example
 * formatMinutesDuration(90) // "1 hr 30 min"
 * formatMinutesDuration(90, { format: "full" }) // "1 hour 30 minutes"
 * formatMinutesDuration(90, { format: "abbreviated" }) // "1 h 30 m"
 * formatMinutesDuration(60) // "1 hr"
 * formatMinutesDuration(45) // "45 min"
 * formatMinutesDuration(0) // "0 min"
 * formatMinutesDuration(-90) // "-1 hr 30 min"
 */
export function formatMinutesDuration(
  durationInMinutes: number,
  options: FormatMinutesDurationOptions = {}
): string {
  const { format = "short", separator = " " } = options;

  const isNegative = durationInMinutes < 0;
  const absolute = Math.abs(durationInMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  const labels = UNIT_LABELS[format];

  const parts: string[] = [];

  if (hours > 0) {
    const unitLabel = format === "full" ? pluralize(labels.hour, hours) : labels.hour;
    parts.push(`${hours}${separator}${unitLabel}`);
  }

  if (minutes > 0 || parts.length === 0) {
    const unitLabel = format === "full" ? pluralize(labels.minute, minutes) : labels.minute;
    parts.push(`${minutes}${separator}${unitLabel}`);
  }

  const result = parts.join(" ");
  return isNegative ? `-${result}` : result;
}
